import { FC } from "react";

import { Typography } from "@mui/material";

import Prompt from "../Prompt";
import Form from "../Form/Form";
import { PersonInfo, PromptInfo, Step } from "../types";
import PhoneLogin from "../Authorization/PhoneLogin";
import { EsignConsent } from "../EsignConsent/EsignConsent";
import VerificationCode from "../Authorization/VerificationCode";
import PersonalDataAuth from "../PersonalDataAuth/PersonalDataAuth";
import { PassportReminder } from "../PassportReminder/PassportReminder";
import { usePatientDataContext } from "../../contexts/PatientDataContext";
import { SendVerificationCode } from "../Authorization/SendVerificationCode";
import { LoopDocumentRetriever } from "../LoopDocumentRetriever/LoopDocumentRetriever";
import { PassportPhotoPickerComponent } from "../PhotoPassportComponent/PassportPhotoPickerComponent";
type StepComponentProps = {
  submitForm?: (data: any) => void;
  login?: (data: any) => Promise<void>;
  loginWithPhone?: (phoneNumber: string) => Promise<void>;
  loginWithPersonalInfo?: (personInfo: PersonInfo) => Promise<void>;
  loginWithSendCode?: (sendCode: boolean) => Promise<void>;
  loginWithVerificationCode?: (verificationCode: string) => Promise<void>;
  setStep: (data: any) => void;
  setPromptInfo?: (promptInfo: PromptInfo) => void;
  promptInfo?: any;
  isMobile?: boolean;
  logger?: string;
};

export const GetStepComponent: FC<{
  step: Step;
  props: StepComponentProps;
}> = ({ step, props }) => {
  const { sessionId, verificationCodes, patientId, phoneNumber } =
    usePatientDataContext();

  const relogin = () => {
    if (!props.login) return;

    props.login({
      sessionId: sessionId,
      verificationCode: verificationCodes,
      pid: patientId,
      phoneNumber: phoneNumber,
    });
  };

  switch (step) {
    case Step.DOCUMENTS_FORM:
      return (
        <LoopDocumentRetriever
          contractId={props.promptInfo?.contractId}
          setStep={props.setStep}
          setPromptInfo={props.setPromptInfo}
        />
      );
    case Step.PASSPORT_FORM:
      return <PassportPhotoPickerComponent setStep={props.setStep} />;
    case Step.PASSPORT_REMINDER:
      return <PassportReminder setStep={props.setStep} />;
    case Step.ESIGN_CONSENT:
      if (!props.login) return <></>;
      return <EsignConsent login={props.login} />;
    case Step.NEW_PROFILE_FORM:
    case Step.QUESTIONNAIRE_FORM:
      if (props.submitForm && props.login) {
        return (
          <Form
            onSubmit={props.submitForm}
            step={step}
            onInitialization={props.login}
            logger={props.logger}
          />
        );
      }
      return <></>;
    case Step.QUESTIONNAIRE_SUBMITTED_SUCCESSFULLY:
      return (
        <>
          <Typography variant="h6">Thank you!</Typography>
          <Typography>
            Your information has been submitted successfully.
          </Typography>
        </>
      );
    case Step.QUESTIONNAIRE_SUBMITTED_ERROR:
      return (
        <>
          <Typography variant="h6">Oops!</Typography>
          <Typography>Something went wrong. Please try again later.</Typography>
        </>
      );
    case Step.PHONE_AUTHORIZATION:
      if (props.loginWithPhone) {
        return <PhoneLogin onLogin={props.loginWithPhone} />;
      }
      return <></>;
    case Step.SEND_VERIFICATION_CODE:
      if (!props.loginWithSendCode) return <></>;
      return (
        <SendVerificationCode
          onLogin={props.loginWithSendCode}
          phoneNumberLast4={props.promptInfo.phoneNumberLast4}
        />
      );
    case Step.VERIFICATION_CODE:
      if (!props.promptInfo || !props.loginWithVerificationCode) return <></>;
      return (
        <VerificationCode
          onLogin={props.loginWithVerificationCode}
          promptInfo={props.promptInfo}
        />
      );
    case Step.PERSONAL_DATA:
      if (!props.loginWithPersonalInfo) return <></>;
      return (
        <PersonalDataAuth
          onLogin={props.loginWithPersonalInfo}
          promptInfo={props.promptInfo}
        />
      );
    case Step.PROMPT:
      if (!props.promptInfo || !props.setStep) return <></>;
      let onOkAction: (() => void) | undefined = undefined;
      // Anything still to do is decided by the server: OK re-authenticates and
      // takes back whatever contract comes next. The kiosk keeps its shortcut
      // straight into the documents the questionnaire just unlocked.
      if (
        props.promptInfo.reloginOnOk ||
        (props.promptInfo.questionnaire_submitted &&
          props.promptInfo.authorized &&
          props.promptInfo.nextStep !== Step.DOCUMENTS_FORM)
      ) {
        onOkAction = relogin;
      }

      let onSecondaryAction: (() => void) | undefined = undefined;
      if (props.promptInfo.secondaryLabel) {
        // "I'll do this later" ends the session outright - a fresh visit to the
        // site starts over with nothing remembered.
        onSecondaryAction = () => {
          window.location.href = "/";
        };
      }

      return (
        <Prompt
          onSwitchStep={props.setStep}
          promptInfo={props.promptInfo}
          onOkAction={onOkAction}
          onSecondaryAction={onSecondaryAction}
        />
      );
    default:
      return <></>;
  }
};
